import { CombinedGraphQLErrors } from "@apollo/client/errors";
import type { IntlShape } from "react-intl";
import { logDiagnostic } from "./diagnostics";
import { CODES } from "@/lib/diagnostics";

export function logApplicationFailure(
  action: string,
  options: {
    code?: string;
    level?: "warn" | "error";
    correlationId?: string;
  } = {},
): void {
  logDiagnostic({
    level: options.level ?? "error",
    code: options.code ?? CODES.application.OPERATION_FAILURE,
    category: "application",
    correlationId: options.correlationId,
    details: { action },
  });
}

// Maps any caught error to a user-safe, localized message (see ADR-0010).
// Raw server messages (graphql-java internals, schema paths, persistence
// details) must never reach the UI: they expose backend logic and are not
// localized. The technical error goes to the console for diagnosis; genesis's
// errorId (GraphQL extensions) is surfaced as a support code when present.
//
// Validation issues from Validate*Result payloads are NOT routed through
// here - those messages are product copy meant for users.
export function userErrorMessage(intl: IntlShape, error: unknown): string {
  // Application-level diagnostic for a user-visible caught failure. The Apollo
  // error link remains the transport source of truth (graphql/network); this
  // records the distinct application perspective with a stable code and no raw
  // error content. Dev keeps the console for local diagnosis (ADR-0010).
  logApplicationFailure("user-visible-error");
  if (import.meta.env.DEV) console.error("[user-error]", error);
  const code = extractErrorId(error);
  return code
    ? intl.formatMessage(
        {
          id: "common.serverErrorWithCode",
          defaultMessage: "Something went wrong. Please try again. (Code {code})",
        },
        { code },
      )
    : intl.formatMessage({
        id: "common.serverError",
        defaultMessage: "Something went wrong. Please try again.",
      });
}

function extractErrorId(error: unknown): string | null {
  // Apollo v4: `ApolloError` was removed; the wrapper for server-returned
  // GraphQL errors is now `CombinedGraphQLErrors`. Its `.errors` field holds
  // the GraphQLFormattedError[] (was `.graphQLErrors` in v3).
  if (!CombinedGraphQLErrors.is(error)) return null;
  for (const g of error.errors) {
    const id = g.extensions?.["errorId"];
    if (typeof id === "string") return id;
  }
  return null;
}
